import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { Message } from './types';

/** Non-text messages are stored with a bracketed placeholder body; the type label reads better. */
function isPlaceholder(body: string): boolean {
  return body === '' || body.startsWith('[') || body.startsWith('{');
}

/**
 * WhatsApp's "reply to" strip at the top of a bubble: who wrote the quoted message and a short
 * excerpt. `quoted` is null when the referenced message is not among the loaded pages; the strip
 * then says only that an earlier message was quoted, instead of hiding that this was a reply.
 */
export function QuotedMessage({ quoted, inOutbound }: { quoted: Message | null; inOutbound: boolean }) {
  const { t } = useTranslation('conversations');

  let author: string | null = null;
  let text: string;
  if (quoted === null) {
    text = t('messageView.quote.unavailable');
  } else {
    author = quoted.direction === 'outbound' ? t('messageView.quote.you') : t('messageView.quote.customer');
    const body = quoted.caption?.trim() || quoted.content?.trim() || '';
    text = isPlaceholder(body)
      ? t(`messageView.messageTypes.${quoted.messageType || 'text'}`, { defaultValue: quoted.messageType })
      : body;
  }

  return (
    <div
      className={cn(
        'mb-1 rounded-md border-l-4 border-chat-accent px-2 py-1 text-[13px] leading-snug',
        inOutbound ? 'bg-chat-bubble-overlay' : 'bg-chat-panel',
      )}
    >
      {author && <div className="text-xs font-medium text-chat-accent-deep">{author}</div>}
      <div className={cn('line-clamp-2 break-words text-chat-text-muted', quoted === null && 'italic')}>{text}</div>
    </div>
  );
}
